import { Button, message, Modal } from "antd";
import { useEffect, useState } from "react";
import { stepValidations } from "../utils/StepsDataValidation";
import { DataCollectionApi } from "../Api/api.DataCollection";
import { CheckCircleOutlined } from "@ant-design/icons";
import { IUseDataCollectionProps } from "../apps/Sections/DataCollection/IDataCollection";

const emptyLegalEntity = {
  beneficialOwners: [{}],
  representatives: [{}],
  contacts: [{}],
  foundsOrigin: [{}],
};

const emptyPhysicalPerson = {
  contacts: [{}],
  foundsOrigin: [{}],
};

const useDataCollection = (props: IUseDataCollectionProps) => {
  const { translations, navigate, setHasDataToComplete } = props;
  const [step, setStep] = useState<number>(0);
  const [investorsList, setInvestorsList] = useState<any[]>([]);
  const [personType, setPersonType] = useState<number>(1); // 1 phisycal, 2 for legal entity
  const [selectedInvestor, setSelectedInvestor] = useState<any>(null);
  const [hasErrors, setHasErrors] = useState<any>({});
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  useEffect(() => {
    getInstitutions();
  }, []);

  const getInstitutions = async () => {
    setIsFetchingData(true);
    await DataCollectionApi.getInstitutionsLinkedToContact()
      .then((response: any) => {
        if (response?.data) {
          setInvestorsList(response.data);
          if (response.data?.length === 0) {
            setHasDataToComplete(false);
          }
        } else {
          setInvestorsList([]);
          setHasDataToComplete(false);
        }
        setIsFetchingData(false);
      })
      .catch((error: any) => {
        message.error("Error fetching institutions");
        setIsFetchingData(false);
      });
  };

  const onChangeInvestor = (
    value: any,
    field: string,
    index?: number,
    arrayField?: string
  ) => {
    if (field === "idInstitution") {
      const institution = investorsList.find(
        (investor) => investor.idInstitution === value
      );
      setSelectedInvestor({
        ...institution,
        ...(institution.idPersonType === 2
          ? emptyLegalEntity
          : emptyPhysicalPerson),
        ...(institution.idPersonType === 1 &&
          !institution.signatories?.length && { signatories: [{}] }),
        idInstitution: value,
      });
      setPersonType(institution.idPersonType);
      return;
    }

    setSelectedInvestor((prev: any) => {
      const updated = { ...prev };

      if (arrayField !== undefined && typeof index === "number") {
        const arr = [...(updated[arrayField] || [])];
        const item = { ...arr[index], [field]: value };
        setNestedValue(item, field, value); // 👈 usa la función auxiliar
        arr[index] = item;
        updated[arrayField] = arr;
      } else {
        setNestedValue(updated, field, value); // 👈 usa la función auxiliar
      }

      return updated;
    });

    // Validación en tiempo real
    validateField(value, field, index, arrayField);
  };

  const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.split(".");
    let current = obj;

    keys.slice(0, -1).forEach((key) => {
      if (!current[key]) current[key] = {};
      current = current[key];
    });

    current[keys.at(-1)!] = value;
  };

  const validateField = (
    value: any,
    field: string,
    index?: number,
    arrayField?: string
  ) => {
    setHasErrors((prevErrors: any) => {
      const updatedErrors = { ...prevErrors };
      const stepErrors = updatedErrors[step]?.[personType] || {};
      let target = stepErrors;

      if (arrayField !== undefined && typeof index === "number") {
        if (!target[arrayField]) target[arrayField] = {};
        if (!target[arrayField][index]) target[arrayField][index] = {};

        if (value && value.toString().trim() !== "") {
          // ✅ Campo válido → limpiar error
          delete target[arrayField][index][field];
          if (Object.keys(target[arrayField][index]).length === 0) {
            delete target[arrayField][index];
          }
        } else {
          // ❌ Campo inválido → marcar error
          if (!target[arrayField][index]) target[arrayField][index] = {};
          target[arrayField][index][field] = true;
        }
      } else {
        if (value && value.toString().trim() !== "") {
          // ✅ Campo válido → limpiar error
          delete target[field];
        } else {
          // ❌ Campo inválido → marcar error
          target[field] = true;
        }
      }

      // 🔥 Limpiar paso/personType si no quedan errores
      if (Object.keys(stepErrors).length === 0) {
        delete updatedErrors[step]?.[personType];
      } else {
        updatedErrors[step] = {
          ...updatedErrors[step],
          [personType]: target,
        };
      }

      return updatedErrors;
    });
  };

  const isValidEmail = (email: string): boolean => {
    const trimmedEmail = email.trim(); // 💥 quitamos espacios
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(trimmedEmail);
  };

  const onAddItem = (arrayField: string) => {
    setSelectedInvestor((prev: any) => {
      const updated = { ...prev };
      const arr = [...(updated[arrayField] || [])];
      arr.push({}); // Add a new empty object
      updated[arrayField] = arr;
      return updated;
    });
  };

  const onRemoveItem = (arrayField: string, index: number) => {
    setSelectedInvestor((prev: any) => {
      const updated = { ...prev };
      const arr = [...(updated[arrayField] || [])];
      arr.splice(index, 1); // Remove the item at the specified index
      updated[arrayField] = arr;
      return updated;
    });
  };

  const handleNextStep = (currentStep: number, personType: number) => {
    const newHasErrors: any = {
      ...hasErrors, // mantenemos errores de otros pasos
      [currentStep]: {
        ...hasErrors[currentStep],
        [personType]: {},
      },
    };

    let foundErrors = false;

    const validationsForStep = stepValidations[currentStep]?.[personType] || [];

    validationsForStep.forEach(({ field, arrayName, conditional }) => {
      if (conditional && !selectedInvestor.ocdeInvestor) {
        return;
      }

      if (arrayName) {
        const array = selectedInvestor[arrayName] || [];
        array.forEach((item: any, index: number) => {
          const value = item[field];
          const isInvalid = !value || value.toString().trim() === "";

          newHasErrors[currentStep] ??= {};
          newHasErrors[currentStep][personType] ??= {};
          newHasErrors[currentStep][personType][arrayName] ??= {};
          newHasErrors[currentStep][personType][arrayName][index] ??= {};

          if (isInvalid) {
            foundErrors = true;
            newHasErrors[currentStep][personType][arrayName][index][field] =
              true;
          }
        });

        //  Validación de emails duplicados: solo una vez por array
        if (field === "email" && array?.length > 1) {
          const hasDuplicatedEmails = hasDuplicateEmails(array);
          newHasErrors[currentStep][personType][
            arrayName
          ].checkedForDuplicates ??= {};

          if (hasDuplicatedEmails) {
            foundErrors = true;
            if (
              !newHasErrors[currentStep][personType][arrayName]
                ?.checkedForDuplicates
            ) {
              message.error(translations.duplicateEmails);
            }
          }

          newHasErrors[currentStep][personType][
            arrayName
          ].checkedForDuplicates = true;
        }
      } else {
        const value = field.includes(".")
          ? field.split(".").reduce((obj, key) => obj?.[key], selectedInvestor)
          : selectedInvestor[field];

        const isInvalid = !value || value.toString().trim() === "";

        if (isInvalid) {
          foundErrors = true;
          newHasErrors[currentStep][personType][field] = true;
        }
      }
    });

    setHasErrors(newHasErrors);
    return !foundErrors;
  };

  const hasDuplicateEmails = (array: any[]): boolean => {
    const seen = new Set<string>();
    for (const item of array) {
      const normalizedEmail = item.email?.trim().toLowerCase();

      if (normalizedEmail) {
        if (seen.has(normalizedEmail)) {
          return true; // Encontrado un duplicado
        }
        seen.add(normalizedEmail);
      }
    }
    return false; // No hay duplicados
  };

  const advanceStep = (isLastStep: boolean = false) => {
    const canAdvance = handleNextStep(step, selectedInvestor.idPersonType);

    if (canAdvance) {
      if (isLastStep) {
        const isValid = isDataValid();
        if (!isValid) {
          return;
        }
        Modal.confirm({
          title: translations.confirmation,
          centered: true,
          icon: null,
          className: "data-collection-confirmation-modal",
          content: translations.confirmationText,
          onOk: () => saveData(),
          okText: translations.send,
          cancelText: translations.keepEditing,
          onCancel: () => {},
        });
      } else {
        setStep((prevStep) => prevStep + 1);
      }
    } else {
      message.error("Completa todos los campos obligatorios antes de avanzar.");
    }
  };

  const goBackStep = () => {
    if (step === 1) {
      setSelectedInvestor(null);
    }
    setStep((prevStep) => (prevStep > 0 ? prevStep - 1 : 0));
  };

  const isDataValid = () => {
    const contacts = selectedInvestor?.contacts || [];
    const principalContacts = contacts.filter((contact: any) =>
      contact.communicationType?.includes("Main contact")
    );
    if (principalContacts.length === 0) {
      message.error(translations.onePrincipalContact);
      return false;
    }
    return true;
  };

  const saveData = async () => {
    const canAdvance = handleNextStep(step, selectedInvestor.idPersonType);
    if (!canAdvance) {
      message.error(translations.completeMandatoryFields);
      return;
    }
    const saveApi =
      personType === 1
        ? DataCollectionApi.savePhysicalPersonData
        : DataCollectionApi.saveLegalEntityData;

    await saveApi(selectedInvestor)
      .then((response: any) => {
        if (response?.data) {
          Modal.success({
            centered: true,
            icon: null,
            width: 500,
            footer: null,
            className: "data-recolection-success-modal",
            content: (
              <div>
                <div className="data-success-modal">
                  <p>
                    <CheckCircleOutlined
                      style={{ color: "#B3D615", fontSize: "32px" }}
                    />
                    {translations.dataReceived}
                  </p>
                  <p>{translations.dataReceivedText}</p>
                  <Button
                    type="primary"
                    onClick={async () => {
                      Modal.destroyAll();
                      navigate("/DataCollection");
                      setHasErrors({});
                      setSelectedInvestor(null);
                      setStep(0);
                      await getInstitutions();
                    }}
                  >
                    {translations.goToStart}
                  </Button>
                </div>
              </div>
            ),
          });
        } else {
          message.error(translations.errorSavingData);
        }
      })
      .catch((error: any) => {
        debugger;
        message.error(
          error?.response?.data?.errorMessage || translations.errorSavingData
        );
      })
      .finally(() => {
        setIsFetchingData(false);
      });
  };

  const handleAutocomplete = (
    checked: boolean,
    sourceArrayName: string,
    targetArrayName: string,
    index: number
  ) => {
    const updatedSourceArray = [...selectedInvestor[sourceArrayName]];
    const updatedTargetArray = [...selectedInvestor[targetArrayName]];

    const sourceItem = {
      ...updatedSourceArray[index],
      isAutocompleted: checked,
    };

    if (checked) {
      const alreadyExists = updatedTargetArray.some(
        (item: any) => item.documentNumber === sourceItem.documentNumber
      );
      if (!alreadyExists) {
        updatedTargetArray.push(sourceItem);
      }
    } else {
      const filteredTargetArray = updatedTargetArray.filter(
        (item: any) => item.documentNumber !== sourceItem.documentNumber
      );
      setSelectedInvestor((prev: any) => ({
        ...prev,
        [targetArrayName]: filteredTargetArray,
      }));
      return;
    }

    setSelectedInvestor((prev: any) => ({
      ...prev,
      [targetArrayName]: updatedTargetArray,
    }));
  };

  const handleAutocompleteContacts = (checked: boolean, person: any) => {
    const updatedContacts = [...selectedInvestor.contacts];

    if (checked) {
      const newContact = {
        email: person.email || "",
        phone: person.phone || "",
        documentNumber: person.documentNumber,
        name: person.name || "",
        firstLastName: person.firstLastName || "",
        secondLastName: person.secondLastName || "",
        isAutocompleted: true, // 🔥 flag para bloquear edición
      };

      const alreadyExists = updatedContacts.some(
        (c: any) => c.documentNumber === newContact.documentNumber
      );
      if (!alreadyExists) {
        if (updatedContacts.length === 1 && !updatedContacts[0].name) {
          updatedContacts[0] = newContact;
        } else {
          updatedContacts.push(newContact);
        }
      }
    } else {
      const filteredContacts = updatedContacts.filter(
        (c: any) => c.documentNumber !== person.documentNumber
      );
      setSelectedInvestor((prev: any) => ({
        ...prev,
        contacts: filteredContacts,
      }));
      return;
    }

    setSelectedInvestor((prev: any) => ({
      ...prev,
      contacts: updatedContacts,
    }));
  };

  return {
    investorsList,
    personType,
    onChangeInvestor,
    advanceStep,
    goBackStep,
    step,
    selectedInvestor,
    setSelectedInvestor,
    onAddItem,
    onRemoveItem,
    handleAutocomplete,
    handleAutocompleteContacts,
    hasErrors,
    isFetchingData,
  };
};

export default useDataCollection;
